import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AddIcon from "@mui/icons-material/Add";
import { useEffect, useState } from "react";
import {
  AUDIENCE_BANDS,
  PLAN_LIMITS,
  bandLabel,
  type AudienceBand,
  type PlanMatrix,
  type PlanMatrixProblem,
} from "@shared/plan-matrix";
import { PageHeader } from "../../components/PageHeader";
import { ItemFormSurface } from "../../components/EditDrawer";
import CommitDrawer from "../../components/CommitDrawer";
import { describeError, useToast } from "../../components/toast";
import type { ActionKey } from "../../components/toast/ToastProvider";
import { foyerApi } from "../../http/foyer";
import { tokens } from "../../theme";
import { OwnerGate } from "../../components/supplier/OwnerGate";
import { translateKey, type Translate } from "../../i18n/translate";

/**
 * The provider's offer matrix for organisations (ORG-1,
 * `docs/design/organisation-plans.md`; Sigge 2026-10-06: offers move out of
 * Nenda admin's storefront controller into the provider's own pages).
 *
 * Rows are film levels (1 film, n films, the full library), columns are
 * audience bands (people per screening - a licence size, not logins), and
 * each cell is a YEARLY price ex VAT. A blank cell is not sold. Below it,
 * the one-off film rental: one play window, a price per band.
 *
 * Prices are typed as text and parsed on save, so a half-typed "12 5" does
 * not jump around under the cursor; the server re-validates everything.
 *
 * A plan is added in a modal and edited in a right drawer; the rental and VAT
 * have their own drawer. There is no page-level Save: the matrix is one record
 * on the server (one PUT for every plan, the rental and VAT together,
 * validated as a whole), so each Save sends the LAST SAVED matrix with just
 * that one change applied, and the outcome is a toast. Nothing half-typed
 * elsewhere on the page can ride along, because nothing is typed on the page.
 */

type PriceDraft = Record<AudienceBand, string>;

interface LevelDraft {
  id: string;
  label: string;
  fullLibrary: boolean;
  films: string;
  prices: PriceDraft;
}

interface Draft {
  vatPercent: string;
  levels: LevelDraft[];
  rentalHours: string;
  rentalPrices: PriceDraft;
}

const PROBLEM_KEY: Record<PlanMatrixProblem, string> = {
  levels_too_many: "tooManyLevels",
  level_id: "level_id",
  level_id_duplicate: "level_id_duplicate",
  level_label: "level_label",
  level_films: "level_films",
  level_films_duplicate: "level_films_duplicate",
  price: "price",
  vat: "vat",
  play_window: "play_window",
};

function problemText(t: Translate, problem: PlanMatrixProblem): string {
  return translateKey(
    t,
    `supplier:supplierPlansPage.problems.${PROBLEM_KEY[problem]}`,
    { max: PLAN_LIMITS.levels }
  );
}

const emptyPrices = (): PriceDraft => ({ small: "", medium: "", large: "" });

function toDraft(m: PlanMatrix): Draft {
  const prices = (p: Partial<Record<AudienceBand, number>>): PriceDraft => ({
    small: p.small?.toString() ?? "",
    medium: p.medium?.toString() ?? "",
    large: p.large?.toString() ?? "",
  });
  return {
    vatPercent: (m.vatBps / 100).toString(),
    levels: m.levels.map((l) => ({
      id: l.id,
      label: l.label,
      fullLibrary: l.films === null,
      films: l.films === null ? "" : l.films.toString(),
      prices: prices(l.prices),
    })),
    rentalHours: m.rental.playWindowHours.toString(),
    rentalPrices: prices(m.rental.prices),
  };
}

/** A number from what was typed; `""` stays blank (not sold). */
function num(s: string): number | undefined {
  const t = s.replace(/\s/g, "").replace(",", ".");
  return t === "" ? undefined : Number(t);
}

function toMatrix(d: Draft): PlanMatrix {
  const prices = (p: PriceDraft) => {
    const out: Partial<Record<AudienceBand, number>> = {};
    for (const b of AUDIENCE_BANDS) {
      const v = num(p[b]);
      if (v !== undefined) out[b] = v;
    }
    return out;
  };
  return {
    currency: "SEK",
    vatBps: Math.round((num(d.vatPercent) ?? 0) * 100),
    levels: d.levels.map((l) => ({
      id: l.id,
      label: l.label,
      films: l.fullLibrary ? null : (num(l.films) ?? 0),
      prices: prices(l.prices),
    })),
    rental: {
      playWindowHours: num(d.rentalHours) ?? 0,
      prices: prices(d.rentalPrices),
    },
  };
}

function newLevelId(existing: string[]): string {
  for (;;) {
    const id = `level-${Math.random().toString(36).slice(2, 8)}`;
    if (!existing.includes(id)) return id;
  }
}

const newLevel = (existing: string[]): LevelDraft => ({
  id: newLevelId(existing),
  label: "",
  fullLibrary: false,
  films: "",
  prices: emptyPrices(),
});

/** The plan being added (modal) or edited (drawer). */
interface PlanForm {
  mode: "create" | "edit";
  /** Its row: `levels.length` for a new plan. */
  index: number;
  level: LevelDraft;
}

export default function SupplierPlansPage() {
  const [draft, setDraft] = useState<Draft | null>(null);
  /**
   * Always mounted with a controlled `open`; `form` is replaced when a plan is
   * opened and deliberately NOT cleared on close, so the drawer does not flip
   * into the create modal while it slides out.
   */
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<PlanForm | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editingRental, setEditingRental] = useState(false);
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const { t, i18n } = useTranslation(["supplier", "common"]);
  const lang = i18n.language === "sv" ? "sv" : "en";

  useEffect(() => {
    let live = true;
    foyerApi
      .ownerPlanMatrix()
      .then((r) => live && setDraft(toDraft(r.matrix)))
      .catch(
        (e) => live && setLoadError(e instanceof Error ? e.message : String(e))
      );
    return () => {
      live = false;
    };
  }, []);

  if (loadError) return <Alert severity="error">{loadError}</Alert>;
  if (!draft) return <CircularProgress size={24} />;

  /**
   * Writes the whole matrix with one change applied to the last saved one.
   * Resolves true when stored; a refusal has already been toasted, in words.
   */
  async function persist(
    next: Draft,
    done: string,
    failure: ActionKey,
    failureParams?: Record<string, string>
  ): Promise<boolean> {
    setBusy(true);
    try {
      const r = await foyerApi.saveOwnerPlanMatrix(toMatrix(next));
      setDraft(toDraft(r.matrix));
      toast.success(done);
      return true;
    } catch (e) {
      const problem = (e as { body?: { problem?: PlanMatrixProblem } }).body
        ?.problem;
      toast.error(
        problem
          ? describeError(t, failure, problemText(t, problem), failureParams)
          : describeError(t, failure, e, failureParams)
      );
      return false;
    } finally {
      setBusy(false);
    }
  }

  const openNew = () => {
    setForm({
      mode: "create",
      index: draft.levels.length,
      level: newLevel(draft.levels.map((l) => l.id)),
    });
    setFormOpen(true);
  };
  const openEdit = (i: number) => {
    setForm({ mode: "edit", index: i, level: { ...draft.levels[i]! } });
    setFormOpen(true);
  };
  const setFormLevel = (patch: Partial<LevelDraft>) =>
    setForm((f) => (f ? { ...f, level: { ...f.level, ...patch } } : f));
  /** Saves the form's plan. True when stored, so the form can close. */
  const applyForm = (): Promise<boolean> => {
    if (!form) return Promise.resolve(false);
    const name =
      form.level.label.trim() ||
      t("supplierPlansPage.planFallbackName", { n: form.index + 1 });
    return persist(
      {
        ...draft,
        levels:
          form.mode === "create"
            ? [...draft.levels, form.level]
            : draft.levels.map((l, j) => (j === form.index ? form.level : l)),
      },
      form.mode === "create"
        ? t("supplierPlansPage.planAdded", { name })
        : t("supplierPlansPage.planSaved", { name }),
      form.mode === "create" ? "addPlan" : "savePlan",
      { name }
    );
  };
  const removePlan = async (i: number) => {
    const l = draft.levels[i]!;
    const name =
      l.label.trim() || t("supplierPlansPage.planFallbackName", { n: i + 1 });
    if (!window.confirm(t("supplierPlansPage.removeConfirm", { name }))) return;
    await persist(
      { ...draft, levels: draft.levels.filter((_, j) => j !== i) },
      t("supplierPlansPage.planRemoved", { name }),
      "removePlan",
      { name }
    );
  };

  const priceField = (
    label: string,
    value: string,
    onChange: (v: string) => void,
    testId: string
  ) => (
    <TextField
      size="small"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={t("supplierPlansPage.notSold")}
      inputProps={{
        inputMode: "decimal",
        "aria-label": label,
        "data-testid": testId,
      }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            {t("supplierPlansPage.currencySuffix")}
          </InputAdornment>
        ),
      }}
      sx={{ width: 150 }}
    />
  );

  return (
    <Stack spacing={4} sx={{ maxWidth: 980 }}>
      <PageHeader
        eyebrow={t("supplierPlansPage.eyebrow")}
        title={t("supplierPlansPage.title")}
        subtitle={t("supplierPlansPage.subtitle")}
      />

      <Box>
        <Typography variant="h6" sx={{ mb: 1 }}>
          {t("supplierPlansPage.yearlyPlans")}
        </Typography>
        <Typography variant="bodySm" sx={{ color: tokens.inkMuted, mb: 2 }}>
          {t("supplierPlansPage.yearlyIntro")}
        </Typography>
        <Table size="small" data-testid="plan-matrix">
          <TableHead>
            <TableRow>
              <TableCell>{t("supplierPlansPage.columns.title")}</TableCell>
              <TableCell>{t("supplierPlansPage.columns.films")}</TableCell>
              {AUDIENCE_BANDS.map((b) => (
                <TableCell key={b}>{bandLabel(b, lang)}</TableCell>
              ))}
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {draft.levels.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} sx={{ color: tokens.inkMuted }}>
                  {t("supplierPlansPage.noPlans")}
                </TableCell>
              </TableRow>
            )}
            {draft.levels.map((l, i) => (
              <TableRow
                key={l.id}
                data-testid={`plan-level-${i}`}
                hover
                onClick={() => openEdit(i)}
                sx={{ cursor: "pointer" }}
              >
                <TableCell>
                  {l.label || (
                    <Box component="span" sx={{ color: tokens.inkMuted }}>
                      {t("supplierPlansPage.untitled")}
                    </Box>
                  )}
                </TableCell>
                <TableCell>
                  {l.fullLibrary ? t("supplierPlansPage.fullLibrary") : l.films}
                </TableCell>
                {AUDIENCE_BANDS.map((b) => (
                  <TableCell key={b}>
                    {l.prices[b].trim() ? (
                      t("supplierPlansPage.price", { amount: l.prices[b] })
                    ) : (
                      <Box component="span" sx={{ color: tokens.inkMuted }}>
                        {t("supplierPlansPage.notSold")}
                      </Box>
                    )}
                  </TableCell>
                ))}
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  <OwnerGate resource="storefront" level="edit">
                    <IconButton
                      aria-label={t("supplierPlansPage.editPlanAria", {
                        n: i + 1,
                      })}
                      onClick={(e) => {
                        e.stopPropagation();
                        openEdit(i);
                      }}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      aria-label={t("supplierPlansPage.removePlanAria", {
                        n: i + 1,
                      })}
                      disabled={busy}
                      onClick={(e) => {
                        e.stopPropagation();
                        void removePlan(i);
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </OwnerGate>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <OwnerGate resource="storefront" level="edit">
          <Button
            startIcon={<AddIcon />}
            sx={{ mt: 1 }}
            disabled={draft.levels.length >= PLAN_LIMITS.levels}
            onClick={openNew}
          >
            {t("supplierPlansPage.addPlan")}
          </Button>
        </OwnerGate>
      </Box>

      {form && (
        <ItemFormSurface
          mode={form.mode}
          open={formOpen}
          onClose={() => setFormOpen(false)}
          title={
            form.mode === "create"
              ? t("supplierPlansPage.addPlan")
              : t("supplierPlansPage.editPlan")
          }
          subtitle={t("supplierPlansPage.savesStraightAway")}
          maxWidth="sm"
          data-testid="plan-form"
          actions={(dismiss) => (
            <>
              <Button onClick={() => dismiss()}>
                {t("common:buttons.cancel")}
              </Button>
              <OwnerGate
                resource="storefront"
                level="edit"
                deniedProps={{ disabled: true }}
              >
                <Button
                  variant="contained"
                  disabled={busy}
                  onClick={async () => {
                    if (await applyForm()) dismiss();
                  }}
                  data-testid="plan-form-apply"
                >
                  {form.mode === "create"
                    ? t("supplierPlansPage.addPlan")
                    : t("common:buttons.save")}
                </Button>
              </OwnerGate>
            </>
          )}
        >
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <TextField
              size="small"
              label={t("supplierPlansPage.columns.title")}
              value={form.level.label}
              onChange={(e) => setFormLevel({ label: e.target.value })}
              placeholder={t("supplierPlansPage.titlePlaceholder")}
              inputProps={{
                "aria-label": t("supplierPlansPage.planTitleAria", {
                  n: form.index + 1,
                }),
              }}
              fullWidth
            />
            <Stack direction="row" alignItems="center" spacing={1}>
              <TextField
                size="small"
                label={t("supplierPlansPage.columns.films")}
                value={form.level.films}
                disabled={form.level.fullLibrary}
                onChange={(e) => setFormLevel({ films: e.target.value })}
                inputProps={{
                  inputMode: "numeric",
                  "aria-label": t("supplierPlansPage.planFilmsAria", {
                    n: form.index + 1,
                  }),
                }}
                sx={{ width: 120 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={form.level.fullLibrary}
                    onChange={(e) =>
                      setFormLevel({ fullLibrary: e.target.checked })
                    }
                  />
                }
                label={t("supplierPlansPage.fullLibrary")}
              />
            </Stack>
            <Box>
              <Typography
                variant="bodySm"
                sx={{ color: tokens.inkMuted, mb: 1 }}
              >
                {t("supplierPlansPage.yearlyPriceHint")}
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                {AUDIENCE_BANDS.map((b) => (
                  <Stack key={b} spacing={0.5}>
                    <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
                      {bandLabel(b, lang)}
                    </Typography>
                    {priceField(
                      t("supplierPlansPage.planPriceAria", {
                        n: form.index + 1,
                        band: bandLabel(b, lang),
                      }),
                      form.level.prices[b],
                      (v) =>
                        setFormLevel({
                          prices: { ...form.level.prices, [b]: v },
                        }),
                      `price-${form.index}-${b}`
                    )}
                  </Stack>
                ))}
              </Stack>
            </Box>
          </Stack>
        </ItemFormSurface>
      )}

      <Box>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1 }}
        >
          <Typography variant="h6">
            {t("supplierPlansPage.rentalAndVat")}
          </Typography>
          <OwnerGate resource="storefront" level="edit">
            <Button
              onClick={() => setEditingRental(true)}
              data-testid="rental-edit"
            >
              {t("common:buttons.edit")}
            </Button>
          </OwnerGate>
        </Stack>
        <Typography variant="bodySm" sx={{ color: tokens.inkMuted, mb: 2 }}>
          {t("supplierPlansPage.rentalIntro")}
        </Typography>
        <Table size="small" data-testid="rental-summary">
          <TableBody>
            <TableRow>
              <TableCell>{t("supplierPlansPage.rentalWindow")}</TableCell>
              <TableCell>
                {t("supplierPlansPage.hours", { hours: draft.rentalHours })}
              </TableCell>
            </TableRow>
            {AUDIENCE_BANDS.map((b) => (
              <TableRow key={b}>
                <TableCell>
                  {t("supplierPlansPage.rentalBand", {
                    band: bandLabel(b, lang),
                  })}
                </TableCell>
                <TableCell>
                  {draft.rentalPrices[b].trim()
                    ? t("supplierPlansPage.price", {
                        amount: draft.rentalPrices[b],
                      })
                    : t("supplierPlansPage.notSold")}
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell>{t("supplierPlansPage.vat")}</TableCell>
              <TableCell>
                {t("supplierPlansPage.percent", { value: draft.vatPercent })}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>

      {editingRental && (
        <RentalDrawer
          initial={draft}
          priceField={priceField}
          onClose={() => setEditingRental(false)}
          onSave={(next) =>
            persist(
              next,
              t("supplierPlansPage.rentalSaved"),
              "saveRentalAndVat"
            )
          }
        />
      )}
    </Stack>
  );
}

function RentalDrawer({
  initial,
  priceField,
  onClose,
  onSave,
}: {
  initial: Draft;
  priceField: (
    label: string,
    value: string,
    onChange: (v: string) => void,
    testId: string
  ) => React.ReactNode;
  onClose: () => void;
  onSave: (next: Draft) => Promise<boolean>;
}) {
  const [d, setD] = useState<Draft>(initial);
  const { t, i18n } = useTranslation(["supplier", "common"]);
  const lang = i18n.language === "sv" ? "sv" : "en";
  return (
    <CommitDrawer
      title={t("supplierPlansPage.rentalDrawerTitle")}
      onClose={onClose}
      onSave={() => onSave(d)}
      data-testid="rental-drawer"
    >
      <Stack spacing={2.5}>
        <TextField
          size="small"
          label={t("supplierPlansPage.rentalWindow")}
          value={d.rentalHours}
          onChange={(e) => setD({ ...d, rentalHours: e.target.value })}
          inputProps={{ inputMode: "numeric" }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {t("supplierPlansPage.hoursSuffix")}
              </InputAdornment>
            ),
          }}
          sx={{ width: 170 }}
        />
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          {AUDIENCE_BANDS.map((b) => (
            <Stack key={b} spacing={0.5}>
              <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
                {bandLabel(b, lang)}
              </Typography>
              {priceField(
                t("supplierPlansPage.rentalPriceAria", {
                  band: bandLabel(b, lang),
                }),
                d.rentalPrices[b],
                (v) =>
                  setD({
                    ...d,
                    rentalPrices: { ...d.rentalPrices, [b]: v },
                  }),
                `rental-${b}`
              )}
            </Stack>
          ))}
        </Stack>
        <TextField
          size="small"
          label={t("supplierPlansPage.vat")}
          value={d.vatPercent}
          onChange={(e) => setD({ ...d, vatPercent: e.target.value })}
          inputProps={{ inputMode: "decimal" }}
          InputProps={{
            endAdornment: <InputAdornment position="end">%</InputAdornment>,
          }}
          helperText={t("supplierPlansPage.vatHelp")}
          sx={{ width: 170 }}
        />
      </Stack>
    </CommitDrawer>
  );
}
